import React from "react";
import { Alert } from 'react-bootstrap';
import FullQuoteActions from "../../state/fullQuoteActions";
import QuoteStepBase from '../../components/QuoteStepBase'
import Address from "../../components/Address";
import ButtonContinue from "../../components/ButtonContinue";
import { IDriverStepProps, IQuoteStepState } from "../../components/interfaces";
import { IAddress, IAddressChangeResult, IPerson, IQuoteApiClient, ILOBApiClient, IPaths, IContactInfo } from "../../classes/Domain";
import { IAppState } from "../../state/interfaces";
import { FULL_QUOTE_STEPS, COMPONENTS } from "../../AppConstants";
import { connect } from "react-redux";
import { validationUtils } from "../../validation/Validation";
import { QuoteStep } from "./QuoteStepLayout";
import Utilities from "../../classes/Utilities";
import QuoteActions from "../../state/appActions";
import { ValidatableForm } from "../../components/ValidatableForm";
import { ValidatableFormGroup } from "../../components/ValidatableFormGroup";
import { IUpdateApplicantResult } from "../../classes/ExternalQuote";
import _ from "lodash";

interface IState extends IQuoteStepState {
    address: IAddress | null,
    invalidAddress: boolean,
    contactInfo: IContactInfo,
    appStateMounted: boolean,
    updateResult: IUpdateApplicantResult | null,
}

interface IProps extends IDriverStepProps {
    quoteId: number,
    applicant: IPerson,
    stateCode: string,
    zipCode: string,
    contactInfo: IContactInfo,
    isNonOwners: boolean,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class YourHome extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);
        this.onAddressChanged = this.onAddressChanged.bind(this);
        this.handleEmailChange = this.handleEmailChange.bind(this);

        this.state = {
            address: null,
            updatingStore: false,
            errors: null,
            invalidAddress: true,
            contactInfo: this.props.contactInfo,
            appStateMounted: false,
            updateResult: null
         };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (prevState.appStateMounted) return prevState;
        if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
            const applicant = nextProps.applicant;
            nextProps.meta.propsSource = "";

            if (null === applicant) return prevState;
            let address = applicant.address;

            const stateCode = !(nextProps.applicant.address && nextProps.applicant.address.stateCode) ? nextProps.stateCode : nextProps.applicant.address.stateCode;
            const zipCode = !(nextProps.applicant.address && nextProps.applicant.address.zipCode) ? nextProps.zipCode : nextProps.applicant.address.zipCode;

            address.stateCode = stateCode;
            address.zipCode = zipCode;

            return {
                ...prevState,
                address,
                appStateMounted: true
            };
        }

        return prevState;
    }

    next(event : React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        this.setState({ updatingStore: true }, () => {
            const updatedAplicant = {
                ...this.props.applicant,
                address: this.state.address
            };

            this.props.updateAppState({
                quoteId: this.props.quoteId,
                applicant: updatedAplicant,
                contactInfo: this.state.contactInfo
            })
            .then((response) => {
                this.setState({ updateResult: response.data }, () => {
                    if (!this.state.updateResult?.isSuccess) {
                        this.setState({ ...this.state, updatingStore: false, errors: null });
                    }
                    else {
                        this.setState({ updatingStore: false, errors: null }, () => this.props.history.push(Utilities.formatUrl(this.props.isNonOwners ? this.props.PATHS.FQ_DRIVERS : this.props.PATHS.FQ_CARS, "")));
                    }
                });
            },
            () => {
                this.setState({ ...this.state, updatingStore: false });
            });
        });
    }

    onAddressChanged(result: IAddressChangeResult) {
        this.setState({
            ...this.state, address: result.address, invalidAddress: !(result.hasOwnProperty("isValid") && result.isValid)
        });
    }

    handleEmailChange(value: any) {
        this.setState({
            contactInfo: { ...this.state.contactInfo, email: value }
        }, () => this.setState({ ...this.state, errors: null }));
    }

    render() {
        return (
            <QuoteStep id={this.props.id}>
                <ValidatableForm hasErrors={null != this.state.errors}>
                    <div className="titleArea"><h5>YOUR HOME</h5></div>

                    {
                        this.state.updateResult && !this.state.updateResult.isSuccess && this.state.updateResult.errors
                            ? <Alert variant={"danger"}>
                                <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
                                <p>Please review errors below</p>
                                <ul>
                                    {
                                        this.state.updateResult.errors.map((error) => (
                                            <li key={Utilities.uniqueId()}>
                                                <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                                            </li>
                                        ))
                                    }
                                </ul>
                            </Alert>
                            : null
                    }

                    <Address label="Address"
                        errors={validationUtils.extractErrorsByPrefix(this.state.errors, "address.")}
                        value={this.state.address}
                        zipCodeDisabled={false}
                        onChange={this.onAddressChanged} />

                    <ValidatableFormGroup
                        disabled={this.state.updatingStore}
                        label="Email address"
                        value={this.state.contactInfo.email}
                        placeholder="Enter email"
                        validated={null !== this.state.errors}
                        validationError={validationUtils.getValidationErrorFor(this.state.errors, "email")}
                        onChange={(e) => { this.handleEmailChange(e.target.value) }} />

                    <div className="btnContainer">
                        <ButtonContinue
                            disabled={this.state.updatingStore || this.state.invalidAddress || !this.state.contactInfo?.email}
                            showLoading={this.state.updatingStore}
                            onClick={this.next} />
                    </div>
                </ValidatableForm>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: IProps) => {
    var applicant = state.externalQuote?.insured;

    return {
        quoteId: state.externalQuote?.rtrQuoteId,
        applicant: applicant,
        zipCode: state.zipCode,
        stateCode: state.stateCode,
        contactInfo: state.contactInfo,
        isNonOwners: state.isNonOwners,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE }
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(FULL_QUOTE_STEPS.YOUR_HOME)),
        updateAppState: (update) => dispatch(fullQuoteActions.updateApplicantMailingAddress(update.quoteId, update.applicant, update.contactInfo))
    };
};

export default connect(mapProps, mapDispatch)(YourHome);
